import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Label, Row } from "reactstrap"
import { Formik, Form, Field } from "formik"
import { useNavigate } from "react-router-dom"
import { referralInfoSchema } from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import {
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  earnedPointsForSender: null,
  earnedPointsForReceiver: null,
  pointValidityInDays: null,
}

const ReferralInfo = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Referral Info | Namq")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, isLoading, referralInfo } = useSelector(
    state => state.GlobalSettings
  )

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      19,
      "referral_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      19,
      "referral_3",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    fetchGlobalSettings()
  }, [])

  useEffect(() => {
    if (referralInfo) {
      if (referralInfo && referralInfo.referralInfo) {
        // console.log(referralInfo)
        const {
          earnedPointsForReceiver,
          earnedPointsForSender,
          pointValidityInDays,
        } = referralInfo?.referralInfo
        setFormValues({
          earnedPointsForSender: earnedPointsForSender,
          earnedPointsForReceiver: earnedPointsForReceiver,
          pointValidityInDays: pointValidityInDays,
        })
      }
    }
  }, [referralInfo])

  const fetchGlobalSettings = () => {
    const payload = {
      type: "referralInfo",
    }
    dispatch(getGlobalSettingByType(payload))
  }

  const handleOnSubmit = values => {
    const {
      earnedPointsForSender,
      earnedPointsForReceiver,
      pointValidityInDays,
    } = values

    const payload = {
      id: referralInfo._id,
      type: "referralInfo",
      referralInfo: {
        earnedPointsForSender,
        earnedPointsForReceiver,
        pointValidityInDays,
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={referralInfoSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, touched }) => (
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <CardTitle>{t("Referral Settings")}</CardTitle>
                    <Form>
                      <Row className="mb-4">
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="earnedPointsForSender"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Invite Friend Rewards Points (Sender)")}
                            </Label>
                            <Field
                              name="earnedPointsForSender"
                              type="number"
                              id="earnedPointsForSender"
                              placeholder={t(
                                "Invite Friend Rewards Points (Sender)"
                              )}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.earnedPointsForSender &&
                                errors.earnedPointsForSender
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="earnedPointsForSender" />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="earnedPointsForReceiver"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Welcome Rewards Points (Receiver)")}
                            </Label>
                            <Field
                              name="earnedPointsForReceiver"
                              type="number"
                              id="earnedPointsForReceiver"
                              placeholder={t(
                                "Welcome Rewards Points (Receiver)"
                              )}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.earnedPointsForReceiver &&
                                errors.earnedPointsForReceiver
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="earnedPointsForReceiver" />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="earnedPointsForSender"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Points Validity(In Days)")}
                            </Label>
                            <Field
                              name="pointValidityInDays"
                              type="number"
                              id="pointValidityInDays"
                              placeholder={t("Points Validity(In Days)")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.pointValidityInDays &&
                                errors.pointValidityInDays
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="pointValidityInDays" />
                          </div>
                        </Col>

                        <Col lg={12}>
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              type="button"
                              color="secondary"
                              onClick={() => navigate(-1)}
                            >
                              {t("Cancel")}
                            </Button>
                            {usersPermissions.edit && (
                              <Button
                                className="mx-2"
                                type="submit"
                                color="success"
                                disabled={isLoading}
                              >
                                {t("Save")}
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6} className="">
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle>{t("Referral Settings Hints/Notes")}</CardTitle>
                    <div className="hints mt-3">
                      <div className="single_hint">
                        <p className="fw-bold">
                          1. {t("Invite Friend Rewards Points (Sender)")}
                        </p>
                        <p>
                          {t(
                            "When a sender invites their friend to the app and the friend consents to receive the invite, downloads the app, and uses the invite code, the sender will receive ({{value}}) points after the receiver's first transaction or order.",
                            { value: values.earnedPointsForSender }
                          )}
                        </p>
                      </div>
                      <div className="single_hint">
                        <p className="fw-bold">
                          2. {t("Welcome Rewards Points (Receiver)")}
                        </p>
                        <p>
                          {t(
                            "When a receiver creates an account using the invite/referral code, they will receive ({{value}}) points after their first transaction or order.",
                            { value: values.earnedPointsForReceiver }
                          )}
                        </p>
                      </div>
                      <div className="single_hint">
                        <p className="fw-bold">
                          3. {t("Points Validity(In Days)")}
                        </p>
                        <p>
                          {t(
                            "Number of days the points will be valid after they are credited."
                          )}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default ReferralInfo
